import React, { useState } from 'react';
import { Users, Calendar, HeartHandshake, Mail } from 'lucide-react';

// Using the same webhook from your main page
const GOOGLE_FORM_URL = "https://script.google.com/macros/s/AKfycbwG7kB6pX13DDxT1bxXse7QFj1w8XS3xms19OU2bmLQjzAhZw6FtYonHVT-GRyeSYE5vA/exec";

const CommunityGrant = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    dateTime: '',
    impact: '',
    requestType: 'Community Grant' // This helps you filter in Google Sheets
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      const data = new FormData();
      // Append all form fields to the outgoing payload
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      
      await fetch(GOOGLE_FORM_URL, {
        method: "POST",
        body: data,
        mode: "no-cors",
      });
    } catch (_) {
      // no-cors swallows errors — treat as success
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 px-6 sm:px-12 bg-[#0A0A0C] min-h-screen flex items-center justify-center">
      <div className="max-w-3xl w-full">
        
        <div className="text-center mb-12">
          <HeartHandshake className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-[#D4AF37] mb-4">
            Supporting those who support our community.
          </h1>
          <p className="text-base font-light text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Node was built to give professionals in the Somers, Yorktown, and surrounding areas a sanctuary for deep work. But we also know that the most important work in our community often happens off the clock.
          </p>
        </div>

        <div className="bg-[#121216] rounded-2xl border border-white/[0.08] p-8 sm:p-12 shadow-2xl">
          <div className="mb-10 text-slate-300 font-light text-sm leading-relaxed space-y-4">
          <p>
              To support the organizations that benefit our community, we offer a limited number of <strong className="text-white font-medium">Node Community Grants</strong> each month.
            </p>
            <p>
              This program provides "at-cost" access to our Executive Conference Room during off-peak hours (Weekdays after 6pm and all-day Sundays) for registered 501(c)(3) organizations and local community groups.
            </p>
            <p>
              If your organization's leaders need to meet with their team, hold a meeting with a client or potential donors, or just need a quiet place to be productive for the benefit of our community, we would love to help. Fill out the form below and we'll be in touch shortly.
            </p>
          </div>

          {submitted ? (
             <div className="text-center py-10 border border-[#D4AF37]/20 rounded-xl bg-[#D4AF37]/[0.04]">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-white font-medium mb-1">Request Received</p>
                <p className="text-slate-400 text-sm font-light">We will review your application and be in touch shortly.</p>
              </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Row 1: Name & Email */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#D4AF37] mb-2">Your Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Users className="h-4 w-4 text-slate-500" />
                    </div>
                    <input type="text" id="name" required className="w-full pl-11 pr-4 py-3 bg-transparent border border-white/20 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" placeholder="Jane Doe" onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#D4AF37] mb-2">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-slate-500" />
                    </div>
                    <input type="email" id="email" required className="w-full pl-11 pr-4 py-3 bg-transparent border border-white/20 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" placeholder="jane@example.com" onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  </div>
                </div>
              </div>

              {/* Row 2: Org & Date */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="organization" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#D4AF37] mb-2">Organization</label>
                  <input type="text" id="organization" required className="w-full px-4 py-3 bg-transparent border border-white/20 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" placeholder="Yorktown Troop 123" onChange={(e) => setFormData({...formData, organization: e.target.value})} />
                </div>
                <div>
                  <label htmlFor="dateTime" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#D4AF37] mb-2">Requested Date / Time</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-slate-500" />
                    </div>
                    <input type="text" id="dateTime" required className="w-full pl-11 pr-4 py-3 bg-transparent border border-white/20 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" placeholder="e.g., 3rd Tuesday, 7 PM" onChange={(e) => setFormData({...formData, dateTime: e.target.value})} />
                  </div>
                </div>
              </div>

              {/* Row 3: Impact */}
              <div>
                <label htmlFor="impact" className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#D4AF37] mb-2">How does your group serve the community?</label>
                <textarea id="impact" rows={4} required className="w-full px-4 py-3 bg-transparent border border-white/20 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300" placeholder="Briefly describe your mission..." onChange={(e) => setFormData({...formData, impact: e.target.value})} />
              </div>

              <button type="submit" disabled={submitting} className="w-full py-4 bg-[#D4AF37] text-[#0A0A0C] rounded-full text-sm font-semibold hover:bg-[#E6C65A] hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(212,175,55,0.4)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityGrant;